import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Mail, ShoppingCart, Download, Clock, Loader2 } from 'lucide-react';
import { formatRemaining, getDownloadStatus } from '../lib/downloadStatus';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await customersAPI.get(id);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load customer');
      navigate('/app/customers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  if (!data) return null;
  const { customer, orders } = data;

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/customers')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{customer.name}</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {customer.email}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                <ShoppingCart className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-xl font-semibold">{customer.total_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600">
                <span className="text-lg font-bold">RM</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-xl font-semibold">RM {(customer.total_spent || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Joined</p>
                <p className="text-sm font-medium">{customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders */}
      <Card>
        <CardHeader><CardTitle className="text-base">Orders</CardTitle></CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No orders</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order Ref</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Validity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map(order => {
                  const dl = order.download_info;
                  const status = getDownloadStatus(dl);
                  const toneMap = {
                    success: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                    danger: '',
                    muted: '',
                    none: 'bg-muted text-muted-foreground',
                  };
                  return (
                    <TableRow key={order.id}>
                      <TableCell className="font-mono text-xs">{order.ref}</TableCell>
                      <TableCell>{order.product_name}</TableCell>
                      <TableCell className="font-semibold">RM {(order.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={order.status === 'paid' ? 'default' : order.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {dl ? (
                          <span className="text-sm">
                            {dl.downloads_used}/{dl.max_downloads}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`customer-order-validity-${order.id}`}>
                        {dl ? formatRemaining(dl.expiry_at) : '-'}
                      </TableCell>
                      <TableCell data-testid={`customer-order-status-${order.id}`}>
                        {status.tone === 'danger' ? (
                          <Badge variant="destructive" className="text-[10px]">{status.label}</Badge>
                        ) : status.tone === 'muted' ? (
                          <Badge variant="secondary" className="text-[10px]">{status.label}</Badge>
                        ) : status.tone === 'success' ? (
                          <Badge className={`${toneMap.success} text-[10px]`}>{status.label}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">{status.label}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
